import { FormEvent, useCallback, useMemo, useRef, useState } from "react";
import { FaPlus } from "react-icons/fa";
import { Input } from "../../components/Input";
import { Box } from "../../components/Box";
import { Button } from "../../components/Button";
import CustomTabs from "../../components/CustomTabs";
import { Flex } from "../../components/Flex";
import Topbar from "../../components/Topbar";
import { ProfilesTable } from "../../components/ProfilesTable";
import { Paragraph } from "../../components/Paragraph";
import { useNavigate } from "react-router-dom";
import { ROUTE } from "../../routes";
import {
  HiOutlineSquares2X2,
  HiOutlineUser,
  HiOutlineBuildingOffice2,
} from "react-icons/hi2";
import { useClickOutside } from "../../hooks/useClickOutside";
import { useDashboardView } from "../../hooks/useDashboardView";
import { profilesView } from "../../hooks/dashboardViews/profiles";
import { PAGE_SIZE_OPTIONS } from "../../components/ServerTable";
import { useAuth } from "../../context/AuthContext";

const TAB_TYPES = ["", "individual", "entity"];

const Profiles = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const closeDropdown = useCallback(() => setIsDropdownOpen(false), []);
  const { hasPermission } = useAuth();

  const navigate = useNavigate();

  const optionLists = useMemo(() => ({ pageSizes: PAGE_SIZE_OPTIONS }), []);

  const { view, setValue, setValues } = useDashboardView(profilesView, {
    optionLists,
  });

  const activeTab = view.tab as number;
  const submittedSearchTerm = view.search as string;

  const handleSearchSubmit = (e: FormEvent) => {
    e.preventDefault();
    setValue("search", searchTerm);
  };

  const handleResetSearch = () => {
    setSearchTerm("");
    setValue("search", "");
  };

  const handleSelection = (type: "individual" | "entity") => {
    setIsDropdownOpen(false);

    if (type === "individual") {
      navigate(ROUTE.PROFILE_NEW_PERSON);
    } else {
      navigate(ROUTE.PROFILE_NEW_ENTITY);
    }
  };

  useClickOutside(dropdownRef, isDropdownOpen, closeDropdown);

  const tabLabels = [
    (
      <>
        <HiOutlineSquares2X2 size={16} />
        All
      </>
    ),
    (
      <>
        <HiOutlineUser size={16} />
        Individual
      </>
    ),
    (
      <>
        <HiOutlineBuildingOffice2 size={16} />
        Entity
      </>
    ),
  ];

  return (
    <Box>
      <Topbar title="Profiles" noBorder>
        <Flex justifyContent="space-between">
          <Flex as="form" ml={4} onSubmit={handleSearchSubmit}>
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.currentTarget.value)}
              type="search"
              size="small"
              placeholder="Search"
              maxWidth={250}
              mr={1}
            />

            <Button
              isDisabled={submittedSearchTerm === searchTerm}
              type="submit"
            >
              Search
            </Button>

            {submittedSearchTerm && (
              <Button
                variant="text"
                pl="0px"
                ml={1}
                onClick={handleResetSearch}
              >
                Reset search
              </Button>
            )}
          </Flex>

          <Flex position="relative" mr={4} ref={dropdownRef}>
            <Button
              Icon={FaPlus}
              iconSize="16px"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              isDisabled={!hasPermission("profile.create")}
              title={
                hasPermission("profile.create")
                  ? undefined
                  : "You don't have permission to create profiles"
              }
            >
              New Profile
            </Button>

            {isDropdownOpen && (
              <Box
                style={{
                  position: "absolute",
                  top: "100%",
                  right: 0,
                  background: "white",
                  boxShadow: "0px 4px 6px rgba(0,0,0,0.1)",
                  borderRadius: "12px",
                  zIndex: 10,
                  width: "100%",
                }}
              >
                <Paragraph
                  my={0}
                  style={{ padding: "10px", cursor: "pointer" }}
                  onClick={() => handleSelection("individual")}
                >
                  Person
                </Paragraph>

                <Paragraph
                  my={0}
                  style={{ padding: "10px", cursor: "pointer" }}
                  onClick={() => handleSelection("entity")}
                >
                  Entity
                </Paragraph>
              </Box>
            )}
          </Flex>
        </Flex>
      </Topbar>

      <Flex justifyContent="space-between">
        <CustomTabs
          activeTab={activeTab}
          onTabChange={(index) => setValue("tab", index)}
          customMarginLeft="24px"
          variant="segmented"
          tabs={TAB_TYPES.map((type, index) => ({
            label: tabLabels[index],
            content: activeTab === index && (
              <ProfilesTable
                searchTerm={submittedSearchTerm}
                type={type}
                initialSorting={[{ id: view.sortBy as string, desc: view.sortDesc as boolean }]}
                initialPageSize={view.pageSize as number}
                onSortChange={(sorting) =>
                  setValues({
                    sortBy: sorting[0]?.id ?? "created_on",
                    sortDesc: sorting[0]?.desc ?? true,
                  })
                }
                onPageSizeChange={(pageSize) => setValue("pageSize", pageSize)}
              />
            ),
          }))}
        />
      </Flex>
    </Box>
  );
};

export default Profiles;
